"use client";

import { FormEvent, MouseEvent, RefObject, SyntheticEvent, useState } from "react";
import { TimelineDatePicker } from "@/components/TimelineDatePicker";
import { submitToFormHandler } from "@/lib/form-handler";
import { RECAPTCHA_ACTIONS } from "@/lib/recaptcha";
import { projectTypes, REQUEST_AUDIT_FORM_ACTION } from "@/lib/request-audit";
import styles from "./fv.module.css";

type FormState = "idle" | "submitting" | "success" | "error";

/*
  The mockup's contact dialog, carrying the same seven required fields as the site's
  audit request form because it posts to the same audit-request route. It lives
  inside the page's themed root, so it follows the light and dark toggle.
*/
export function ContactDialog({ dialogRef }: { dialogRef: RefObject<HTMLDialogElement | null> }) {
  const [formState, setFormState] = useState<FormState>("idle");
  // TimelineDatePicker keeps its date in state, which form.reset() can't clear,
  // so closing the dialog remounts the form to start it empty next time.
  const [formKey, setFormKey] = useState(0);

  function reset() {
    setFormState("idle");
    setFormKey((key) => key + 1);
  }

  function closeOnBackdrop(event: MouseEvent<HTMLDialogElement>) {
    if (event.target !== event.currentTarget) return;
    const box = event.currentTarget.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) event.currentTarget.close();
  }

  // Escape closes the date picker's calendar first, not the whole dialog and the
  // form with it. Blurring the field lets the next Escape close the dialog.
  function keepOpenForDatePicker(event: SyntheticEvent<HTMLDialogElement>) {
    const field = document.activeElement;
    if (field instanceof HTMLElement && field.id === "fv-timeline") {
      event.preventDefault();
      field.blur();
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState("submitting");
    try {
      await submitToFormHandler(REQUEST_AUDIT_FORM_ACTION, new FormData(event.currentTarget), RECAPTCHA_ACTIONS.audit);
      setFormState("success");
    } catch {
      setFormState("error");
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="fv-dialog-title"
      onCancel={keepOpenForDatePicker}
      onClose={reset}
      onClick={closeOnBackdrop}
    >
      <div className={styles.dialogInner}>
        <div className={styles.dialogTop}>
          <p className={styles.eyebrow}>Talk to Quantstamp</p>
          <button
            type="button"
            className={styles.dialogClose}
            aria-label="Close contact form"
            onClick={() => dialogRef.current?.close()}
          >
            ×
          </button>
        </div>
        <h2 id="fv-dialog-title" className={styles.dialogTitle}>Get in touch.</h2>
        {formState === "success" ? (
          <p className={styles.dialogIntro} role="status">
            We received your request successfully. Our team will be in touch with you within the next few days.
          </p>
        ) : (
          <>
            <p className={styles.dialogIntro}>Tell us what you need to verify.</p>
            <form key={formKey} className={styles.dialogForm} onSubmit={submit}>
              <div>
                <label htmlFor="fv-full-name">Full Name</label>
                <input id="fv-full-name" name="full_name" type="text" maxLength={256} required autoComplete="name" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="fv-telegram">Telegram</label>
                <input id="fv-telegram" name="telegram" type="text" maxLength={256} required autoComplete="off" />
              </div>
              <div>
                <label htmlFor="fv-email">Email</label>
                <input id="fv-email" name="email" type="email" maxLength={256} required autoComplete="email" placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="fv-project-name">Project Name or Website URL</label>
                <input id="fv-project-name" name="project_name" type="text" maxLength={256} required />
              </div>
              <div>
                <label htmlFor="fv-project-type">Project Type</label>
                <select id="fv-project-type" name="project_type" defaultValue="" required>
                  <option value="" disabled>-- Select --</option>
                  {projectTypes.map(([value, label]) => (
                    <option value={value} key={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="fv-timeline">Timeline</label>
                <TimelineDatePicker id="fv-timeline" name="timeline_deadline" required />
              </div>
              <div className={styles.dialogWide}>
                <label htmlFor="fv-description">Briefly describe the purpose of your project</label>
                <textarea id="fv-description" name="project_description" maxLength={5000} required rows={3} placeholder="What do you need to verify?" />
              </div>
              <button type="submit" className={`${styles.button} ${styles.dialogSubmit}`} disabled={formState === "submitting"}>
                {formState === "submitting" ? "Sending..." : "Send inquiry"} <span className={styles.arrow} aria-hidden="true">↗</span>
              </button>
              {formState === "error" && (
                <p className={styles.dialogStatus} role="alert">Your inquiry could not be sent. Please contact Quantstamp directly.</p>
              )}
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}
